import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CalendarMonth as CalendarMonthIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  getAgencyCommissionPerformance,
  getAgentCommissionPerformance,
  updateCommissionStatus,
  bulkUpdateCommissionStatus,
  type AgencyCommissionPerformance,
  type AgentCommissionPerformance,
} from '../services/commissionService';
import { agencyService, type User } from '../services/agencyService';
import CommissionTimeline from '../components/CommissionTimeline';
import CommissionCalendar from '../components/CommissionCalendar';
import {
  buildExportRows,
  exportCommissionToCsv,
} from '../utils/commissionExport';
import type { CommissionStatus } from '../types/commission';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`performance-tabpanel-${index}`}
      aria-labelledby={`performance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const CommissionPerformancePage: React.FC = () => {
  const { agencySlug } = useParams<{ agencySlug: string }>();
  const navigate = useNavigate();
  const { user, agency } = useAuth();
  const planName = (agency?.subscription?.planName || agency?.subscriptionPlan || 'Free').toLowerCase();
  const hasCommissionAccess = ['professional', 'agency premium', 'premium', 'enterprise', 'demo'].includes(planName);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [agentSelectFocused, setAgentSelectFocused] = useState(false);

  const [agencyPerformance, setAgencyPerformance] = useState<AgencyCommissionPerformance | null>(null);
  const [agentPerformance, setAgentPerformance] = useState<AgentCommissionPerformance | null>(null);
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [timelineYear, setTimelineYear] = useState(() => new Date().getFullYear());
  const [statusUpdatingPropertyId, setStatusUpdatingPropertyId] = useState<string | null>(null);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<Set<string>>(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [settlementFilter, setSettlementFilter] = useState<'all' | 'settlement_set'>('all');
  const [cancelConfirmDialog, setCancelConfirmDialog] = useState<{ propertyId: string; onConfirm: () => void } | null>(null);

  const isAdmin = user?.primaryRole === 'admin';

  const filteredAgencyProperties = useMemo(() => {
    const list = agencyPerformance?.properties ?? [];
    if (settlementFilter !== 'settlement_set') return list;
    return list.filter((p) => p.settlementDate && String(p.settlementDate).trim() !== '');
  }, [agencyPerformance?.properties, settlementFilter]);

  const filteredAgentProperties = useMemo(() => {
    const list = agentPerformance?.properties ?? [];
    if (settlementFilter !== 'settlement_set') return list;
    return list.filter((p) => p.settlementDate && String(p.settlementDate).trim() !== '');
  }, [agentPerformance?.properties, settlementFilter]);

  const filteredAgencyTotals = useMemo(() => {
    const list = filteredAgencyProperties;
    const totalCommission = list.reduce((s, p) => s + p.commission, 0);
    const totalAgentShare = list.reduce((s, p) => s + p.shareAmount, 0);
    const totalAgencyShare = totalCommission - totalAgentShare;
    return {
      totalCommission,
      totalAgencyShare,
      totalAgentShare,
      propertyCount: list.length,
      averageCommission: list.length > 0 ? totalCommission / list.length : 0,
    };
  }, [filteredAgencyProperties]);

  const filteredAgentTotals = useMemo(() => {
    const list = filteredAgentProperties;
    const totalCommission = list.reduce((s, p) => s + p.commission, 0);
    return {
      totalCommission,
      propertyCount: list.length,
    };
  }, [filteredAgentProperties]);

  const loadTeamMembers = async () => {
    if (!agencySlug) return;
    try {
      const members = await agencyService.getTeamMembers(agencySlug);
      // Include all active team members, sorted by position/title
      const activeMembers = members.filter(m => m.isActive !== false);
      
      // Sort by position (or primaryRole as fallback), then by name
      const sortedMembers = activeMembers.sort((a, b) => {
        const aPosition = a.position || a.primaryRole || '';
        const bPosition = b.position || b.primaryRole || '';
        if (aPosition !== bPosition) {
          return aPosition.localeCompare(bPosition);
        }
        const aName = `${a.firstName} ${a.lastName}`;
        const bName = `${b.firstName} ${b.lastName}`;
        return aName.localeCompare(bName);
      });
      
      setTeamMembers(sortedMembers);
    } catch (err) {
      console.error('Failed to load team members:', err);
    }
  };

  const loadAgencyPerformance = useCallback(async () => {
    if (!agencySlug) return;
    try {
      setLoading(true);
      setError(null);
      const result = await getAgencyCommissionPerformance(agencySlug);
      setAgencyPerformance(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load agency performance');
    } finally {
      setLoading(false);
    }
  }, [agencySlug]);

  const loadAgentPerformance = useCallback(async () => {
    if (!agencySlug || !selectedAgentId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await getAgentCommissionPerformance(agencySlug, selectedAgentId);
      setAgentPerformance(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load agent performance');
    } finally {
      setLoading(false);
    }
  }, [agencySlug, selectedAgentId]);

  useEffect(() => {
    if (agencySlug) {
      loadTeamMembers();
    }
  }, [agencySlug]);

  useEffect(() => {
    if (tabValue === 0 || tabValue === 2) {
      loadAgencyPerformance();
    }
  }, [tabValue, loadAgencyPerformance]);

  useEffect(() => {
    if (tabValue === 1 && selectedAgentId) {
      loadAgentPerformance();
    }
  }, [tabValue, selectedAgentId, loadAgentPerformance]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAgentChange = (agentId: string) => {
    setSelectedAgentId(agentId);
    setSelectedPropertyIds(new Set());
  };

  const handleStatusChangeInReport = useCallback(
    async (propertyId: string, status: CommissionStatus) => {
      if (!agencySlug) return;
      try {
        setStatusUpdatingPropertyId(propertyId);
        setError(null);
        await updateCommissionStatus(agencySlug, propertyId, status);
        await loadAgentPerformance();
      } catch (err: any) {
        setError(err.message || 'Failed to update commission status');
      } finally {
        setStatusUpdatingPropertyId(null);
      }
    },
    [agencySlug, loadAgentPerformance]
  );

  const handleStatusChangeInCalendar = useCallback(
    async (propertyId: string, status: CommissionStatus) => {
      if (!agencySlug) return;
      try {
        setStatusUpdatingPropertyId(propertyId);
        setError(null);
        await updateCommissionStatus(agencySlug, propertyId, status);
        await loadAgencyPerformance();
      } catch (err: any) {
        setError(err.message || 'Failed to update commission status');
      } finally {
        setStatusUpdatingPropertyId(null);
      }
    },
    [agencySlug, loadAgencyPerformance]
  );

  const handleBulkStatusInReport = useCallback(
    async (status: CommissionStatus) => {
      if (!agencySlug || selectedPropertyIds.size === 0) return;
      try {
        setBulkUpdating(true);
        setError(null);
        const result = await bulkUpdateCommissionStatus(
          agencySlug,
          Array.from(selectedPropertyIds),
          status
        );
        if (result.failed.length > 0) {
          setError(`Updated ${result.updated}; failed for ${result.failed.length} property(ies).`);
        }
        setSelectedPropertyIds(new Set());
        await loadAgentPerformance();
      } catch (err: any) {
        setError(err.message || 'Failed to bulk update commission status');
      } finally {
        setBulkUpdating(false);
      }
    },
    [agencySlug, selectedPropertyIds, loadAgentPerformance]
  );

  const handleBulkStatusInCalendar = useCallback(
    async (propertyIds: string[], status: CommissionStatus) => {
      if (!agencySlug || propertyIds.length === 0) return;
      try {
        setBulkUpdating(true);
        setError(null);
        const result = await bulkUpdateCommissionStatus(agencySlug, propertyIds, status);
        if (result.failed.length > 0) {
          setError(`Updated ${result.updated}; failed for ${result.failed.length} property(ies).`);
        }
        await loadAgencyPerformance();
      } catch (err: any) {
        setError(err.message || 'Failed to bulk update commission status');
      } finally {
        setBulkUpdating(false);
      }
    },
    [agencySlug, loadAgencyPerformance]
  );

  const handleExportAgencyCsv = useCallback(() => {
    const rows = buildExportRows(
      filteredAgencyProperties.map((p) => ({
        ...p,
        status: (p.status ?? 'pending') as string,
        agentNames: p.agentNames,
      })),
      'all'
    );
    exportCommissionToCsv(
      rows,
      `agency-commission-report-${new Date().toISOString().slice(0, 10)}.csv`
    );
  }, [filteredAgencyProperties]);

  const handleExportAgentCsv = useCallback(() => {
    const rows = buildExportRows(
      filteredAgentProperties.map((p) => ({
        ...p,
        status: (p.status ?? 'pending') as string,
        agentNames: undefined,
      })),
      'all'
    );
    const agent = teamMembers.find((m) => m._id === selectedAgentId);
    const suffix = agent
      ? `${agent.firstName}-${agent.lastName}`.replace(/\s+/g, '-')
      : selectedAgentId;
    exportCommissionToCsv(
      rows,
      `agent-commission-${suffix}-${new Date().toISOString().slice(0, 10)}.csv`
    );
  }, [filteredAgentProperties, selectedAgentId, teamMembers]);

  const handleTimelinePropertyClick = useCallback(
    (propertyId: string) => {
      if (agencySlug) {
        navigate(`/agencies/${agencySlug}/properties/${propertyId}`);
      }
    },
    [agencySlug, navigate]
  );

  if (!hasCommissionAccess) {
    return (
      <Box p={3}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Agency Commission Report
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Commission reports are available on the Agency Premium plan. Upgrade your subscription
              to view agency-wide and per-agent commission performance.
            </Typography>
            <Button
              variant="contained"
              onClick={() => agencySlug && navigate(`/agencies/${agencySlug}/subscription`)}
            >
              View subscription
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
        <Typography variant="h4">Agency Commission Report</Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="settlement-filter-label">Show</InputLabel>
          <Select
            labelId="settlement-filter-label"
            value={settlementFilter}
            label="Show"
            onChange={(e) => setSettlementFilter(e.target.value as 'all' | 'settlement_set')}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="settlement_set">Settlement date set</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Commission is paid at settlement. Reports may include pre-settlement calculations. Use the{' '}
        <strong>Calendar</strong> tab to review pending commissions by month and mark status in one
        place. Timeline and Agency-wide show aggregates.
      </Alert>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            icon={<BusinessIcon />}
            iconPosition="start"
            label="Agency-wide (all agents)"
          />
          <Tab
            icon={<PersonIcon />}
            iconPosition="start"
            label="By agent"
          />
          <Tab
            icon={<CalendarMonthIcon />}
            iconPosition="start"
            label="Calendar"
          />
        </Tabs>
      </Box>

      {/* Agency Performance Tab */}
      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : agencyPerformance ? (
          <Box>
            <Box display="flex" justifyContent="flex-end" sx={{ mb: 1 }}>
              {filteredAgencyProperties.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportAgencyCsv}
                >
                  Export CSV
                </Button>
              )}
            </Box>
            <CommissionTimeline
              properties={filteredAgencyProperties}
              selectedYear={timelineYear}
              onYearChange={setTimelineYear}
              amountKey="commission"
              amountLabel="Commission"
              onPropertyClick={agencySlug ? handleTimelinePropertyClick : undefined}
            />
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total Commission
                    </Typography>
                    <Typography variant="h5" color="primary.main">
                      ${filteredAgencyTotals.totalCommission.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Agency Share
                    </Typography>
                    <Typography variant="h5" color="primary.main">
                      ${filteredAgencyTotals.totalAgencyShare.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Agent Share
                    </Typography>
                    <Typography variant="h5" color="success.main">
                      ${filteredAgencyTotals.totalAgentShare.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Properties Sold
                    </Typography>
                    <Typography variant="h5">
                      {filteredAgencyTotals.propertyCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avg: ${filteredAgencyTotals.averageCommission.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {agencyPerformance.agentPerformance.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Agent Performance Breakdown
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Agent</TableCell>
                          <TableCell align="right">Properties</TableCell>
                          <TableCell align="right">Total Commission</TableCell>
                          <TableCell align="right">Average per Property</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {agencyPerformance.agentPerformance
                          .sort((a, b) => b.totalCommission - a.totalCommission)
                          .map((agent) => (
                            <TableRow key={agent.agentId}>
                              <TableCell>
                                <Box display="flex" alignItems="center">
                                  <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
                                  {agent.agentName}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Chip label={agent.propertyCount} size="small" />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="medium">
                                  ${agent.totalCommission.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="text.secondary">
                                  ${(agent.totalCommission / agent.propertyCount).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}
          </Box>
        ) : (
          <Alert severity="info">
            No commission data. Commission is paid at settlement; add settlement dates to
            properties and calculate commission to see them here.
          </Alert>
        )}
      </TabPanel>

      {/* Agent Performance Tab */}
      <TabPanel value={tabValue} index={1}>
        <Card sx={{ mb: 3, mt: 2 }}>
          <CardContent>
            <FormControl fullWidth>
              <InputLabel 
                id="agent-select-label" 
                shrink={selectedAgentId !== '' || agentSelectFocused}
              >
                Select Agent
              </InputLabel>
              <Select
                labelId="agent-select-label"
                value={selectedAgentId || ''}
                onChange={(e) => handleAgentChange(e.target.value)}
                label="Select Agent"
                onFocus={() => setAgentSelectFocused(true)}
                onBlur={() => setAgentSelectFocused(false)}
                renderValue={(selected) => {
                  if (!selected || selected === '') {
                    // Only show placeholder when focused, otherwise return empty to avoid label overlap
                    if (agentSelectFocused) {
                      return <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>Select an agent...</span>;
                    }
                    return '';
                  }
                  const member = teamMembers.find(m => m._id === selected);
                  if (member) {
                    const isCurrentUser = user?.id === member._id || (user as any)?._id === member._id;
                    return `${member.firstName} ${member.lastName}${isCurrentUser ? ' (You)' : ''}`;
                  }
                  return selected;
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <em>Select an agent...</em>
                </MenuItem>
                {(() => {
                  // Group members by position/title
                  const grouped: Record<string, typeof teamMembers> = {};
                  teamMembers.forEach(member => {
                    const position = member.position || member.primaryRole || 'Other';
                    if (!grouped[position]) {
                      grouped[position] = [];
                    }
                    grouped[position].push(member);
                  });
                  
                  // Render grouped menu items
                  const items: React.ReactNode[] = [];
                  Object.keys(grouped).sort().forEach(position => {
                    items.push(
                      <ListSubheader key={`header-${position}`} sx={{ fontWeight: 700 }}>
                        {position}
                      </ListSubheader>
                    );
                    grouped[position].forEach(member => {
                      const isCurrentUser = user?._id === member._id;
                      items.push(
                        <MenuItem key={member._id} value={member._id}>
                          {member.firstName} {member.lastName}
                          {isCurrentUser && ' (You)'}
                        </MenuItem>
                      );
                    });
                  });
                  return items;
                })()}
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        {!selectedAgentId ? (
          <Alert severity="info">Please select an agent to view performance.</Alert>
        ) : loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : agentPerformance ? (
          <Box>
            <Box display="flex" justifyContent="flex-end" sx={{ mb: 1 }}>
              {filteredAgentProperties.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportAgentCsv}
                >
                  Export CSV
                </Button>
              )}
            </Box>
            <CommissionTimeline
              properties={filteredAgentProperties}
              selectedYear={timelineYear}
              onYearChange={setTimelineYear}
              onPropertyClick={agencySlug ? handleTimelinePropertyClick : undefined}
            />
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total Commission
                    </Typography>
                    <Typography variant="h5" color="success.main">
                      ${filteredAgentTotals.totalCommission.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Properties Sold
                    </Typography>
                    <Typography variant="h5">
                      {filteredAgentTotals.propertyCount}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {filteredAgentProperties.length > 0 && (
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
                    <Typography variant="h6">Property Details</Typography>
                    {isAdmin && selectedPropertyIds.size > 0 && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="text.secondary">
                          {selectedPropertyIds.size} selected
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleBulkStatusInReport('paid')}
                          disabled={bulkUpdating}
                        >
                          Mark selected as paid
                        </Button>
                      </Box>
                    )}
                  </Box>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          {isAdmin && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                indeterminate={
                                  selectedPropertyIds.size > 0 &&
                                  selectedPropertyIds.size < filteredAgentProperties.length
                                }
                                checked={
                                  filteredAgentProperties.length > 0 &&
                                  selectedPropertyIds.size === filteredAgentProperties.length
                                }
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedPropertyIds(
                                      new Set(filteredAgentProperties.map((p) => p.propertyId))
                                    );
                                  } else {
                                    setSelectedPropertyIds(new Set());
                                  }
                                }}
                                aria-label="Select all"
                              />
                            </TableCell>
                          )}
                          <TableCell>Property Address</TableCell>
                          <TableCell align="right">Sold Price</TableCell>
                          <TableCell align="right">Total Commission</TableCell>
                          <TableCell align="right">Agent Share</TableCell>
                          <TableCell>Sold Date</TableCell>
                          <TableCell>Settlement Date</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredAgentProperties.map((property) => (
                          <TableRow key={property.propertyId}>
                            {isAdmin && (
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedPropertyIds.has(property.propertyId)}
                                  onChange={(e) => {
                                    setSelectedPropertyIds((prev) => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.add(property.propertyId);
                                      else next.delete(property.propertyId);
                                      return next;
                                    });
                                  }}
                                  aria-label={`Select ${property.propertyAddress}`}
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              {agencySlug ? (
                                <Link
                                  to={`/agencies/${agencySlug}/properties/${property.propertyId}`}
                                  style={{ color: 'inherit', fontWeight: 500 }}
                                >
                                  {property.propertyAddress}
                                </Link>
                              ) : (
                                property.propertyAddress
                              )}
                            </TableCell>
                            <TableCell align="right">
                              ${property.soldPrice.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell align="right">
                              ${property.commission.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="medium" color="success.main">
                                ${property.shareAmount.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {new Date(property.soldDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </TableCell>
                            <TableCell>
                              {property.settlementDate
                                ? new Date(property.settlementDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {isAdmin ? (
                                <FormControl size="small" sx={{ minWidth: 120 }} disabled={statusUpdatingPropertyId === property.propertyId}>
                                  <Select
                                    value={property.status ?? 'pending'}
                                    onChange={(e) => {
                                      const newStatus = e.target.value as CommissionStatus;
                                      if (newStatus === 'cancelled') {
                                        setCancelConfirmDialog({
                                          propertyId: property.propertyId,
                                          onConfirm: () => handleStatusChangeInReport(property.propertyId, 'cancelled'),
                                        });
                                        return;
                                      }
                                      handleStatusChangeInReport(property.propertyId, newStatus);
                                    }}
                                    displayEmpty
                                  >
                                    <MenuItem value="pending">
                                      <Box display="flex" alignItems="center" gap={0.5}>
                                        <ScheduleIcon fontSize="small" />
                                        Pending
                                      </Box>
                                    </MenuItem>
                                    <MenuItem value="paid">
                                      <Box display="flex" alignItems="center" gap={0.5}>
                                        <CheckCircleIcon fontSize="small" />
                                        Paid
                                      </Box>
                                    </MenuItem>
                                    <MenuItem value="cancelled">
                                      <Box display="flex" alignItems="center" gap={0.5}>
                                        <CancelIcon fontSize="small" />
                                        Cancelled
                                      </Box>
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              ) : (
                                <>
                                  {property.status === 'paid'
                                    ? 'Paid'
                                    : property.status === 'cancelled'
                                      ? 'Cancelled'
                                      : 'Pending'}
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}
          </Box>
        ) : (
          <Alert severity="info">
            No commission data for this agent. Commission is paid at settlement; add settlement
            dates to properties and calculate commission to see them here.
          </Alert>
        )}
      </TabPanel>

      {/* Calendar Tab */}
      <TabPanel value={tabValue} index={2}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : agencyPerformance ? (
          <Box>
            <Box display="flex" justifyContent="flex-end" sx={{ mb: 1 }}>
              {filteredAgencyProperties.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportAgencyCsv}
                >
                  Export CSV
                </Button>
              )}
            </Box>
            <CommissionCalendar
              properties={filteredAgencyProperties}
              agencySlug={agencySlug ?? ''}
              isAdmin={!!isAdmin}
              onStatusChange={(propertyId, status) => {
                if (status === 'cancelled') {
                  setCancelConfirmDialog({
                    propertyId,
                    onConfirm: () => handleStatusChangeInCalendar(propertyId, 'cancelled'),
                  });
                  return Promise.resolve();
                }
                return handleStatusChangeInCalendar(propertyId, status);
              }}
              statusUpdatingPropertyId={statusUpdatingPropertyId}
              onBulkStatusChange={isAdmin ? handleBulkStatusInCalendar : undefined}
              bulkUpdating={bulkUpdating}
            />
          </Box>
        ) : (
          <Alert severity="info">
            No commission data. Commission is paid at settlement; add settlement dates to
            properties and calculate commission to see them in the calendar.
          </Alert>
        )}
      </TabPanel>

      <Dialog open={!!cancelConfirmDialog} onClose={() => setCancelConfirmDialog(null)}>
        <DialogTitle>Mark commission as cancelled?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Only use when the sale did not proceed (e.g. settlement did not complete). This is a rare case, not a normal status like pending or paid.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmDialog(null)}>Keep status</Button>
          <Button
            color="inherit"
            variant="outlined"
            onClick={() => {
              cancelConfirmDialog?.onConfirm();
              setCancelConfirmDialog(null);
            }}
          >
            Yes, mark as cancelled
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommissionPerformancePage;
